import { useMemo } from "react";
import { useScope } from "@state/scopeContext.lib";
import { useWorkspacesList } from "@api/workspace.api";
import { useLedgersByEntityIds } from "@api/ledger.api";
import { buildCopyEntityPrefill, CopyEntityPrefill } from "./copyEntityPrefill";
import { findEntityInWorkspaces } from "./findEntityInWorkspaces";

interface UseCopyEntitySourcePrefillResult {
    prefill: CopyEntityPrefill | null;
    // The entity's legal type + industries come from the already-cached
    // workspace list, so they're ready as soon as the entity resolves — apply
    // them immediately to avoid clobbering edits during the ledgers fetch.
    isHeaderReady: boolean;
    // Basis comes from a real ledgers round-trip; apply it only once resolved.
    isBasisReady: boolean;
}

// Resolves the header prefill for the "Copy from entity" starting point from
// the cached workspace list (legal type + industries) plus a batched ledgers
// fetch (basis), exposing the two readiness signals separately so the caller
// can apply the synchronous fields first and basis when it arrives.
export function useCopyEntitySourcePrefill(
    sourceEntityId: number | null,
): UseCopyEntitySourcePrefillResult {
    const { organizationId } = useScope();
    const { data: workspaces, isFetched: areWorkspacesFetched } =
        useWorkspacesList({
            organizationId: organizationId ?? undefined,
            includeEntities: true,
        });

    const entityIds = useMemo(
        () => (sourceEntityId !== null ? [sourceEntityId] : []),
        [sourceEntityId],
    );
    const { data: ledgersByEntityId, isFetched: areLedgersFetched } =
        useLedgersByEntityIds(entityIds);

    const entity = useMemo(
        () => findEntityInWorkspaces(workspaces, sourceEntityId),
        [workspaces, sourceEntityId],
    );

    const ledgerBases = useMemo(
        () =>
            sourceEntityId !== null
                ? (ledgersByEntityId?.[sourceEntityId] ?? []).map(
                      (ledger) => ledger.basis,
                  )
                : [],
        [ledgersByEntityId, sourceEntityId],
    );

    const prefill = useMemo(
        () =>
            entity
                ? buildCopyEntityPrefill({
                      entity: {
                          legalType: entity.legalType,
                          industries: entity.industries,
                      },
                      ledgerBases,
                  })
                : null,
        [entity, ledgerBases],
    );

    return {
        prefill,
        isHeaderReady:
            sourceEntityId !== null && areWorkspacesFetched && entity !== null,
        isBasisReady: sourceEntityId !== null && areLedgersFetched,
    };
}
